import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const KEYBOARD_CLICK_DETAIL = 0;

export const DropdownContainer = props => {
    const {
        details: { name, className, btnClassName, ariaClassName, text, statesHasFeature, symbol, symbolClassName },
        index,
        environment,
        children,
    } = props;

    const listRef = useRef(null);
    const buttonRef = useRef(null);
    const openByKeyboardRef = useRef(false);
    const [open, setOpen] = useState(false);

    const handleButtonKeyDown = e => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();

            setOpen(true);

            openByKeyboardRef.current = true;
        }
    };

    const onButtonClick = e => {
        setOpen(true);

        openByKeyboardRef.current = e.detail === KEYBOARD_CLICK_DETAIL;
    };

    const closeMenu = useCallback(() => {
        setOpen(false);

        buttonRef.current?.focus();

        openByKeyboardRef.current = false;
    }, []);

    /**
     * Gets the first focusable element within a node
     * @param {HTMLElement} node - The DOM node to search within
     * @returns {HTMLElement|null} The first focusable element or null
     */
    const getFocusableElement = node => {
        return node?.querySelector('a,button,[tabindex]:not([tabindex="-1"])');
    };

    const focusFirstDropdownItem = useCallback(() => {
        if (listRef.current) {
            const first = listRef.current.firstElementChild;

            getFocusableElement(first)?.focus();
        }
    }, []);

    useLayoutEffect(() => {
        if (open && openByKeyboardRef.current) {
            requestAnimationFrame(() => {
                focusFirstDropdownItem();
            });
        }
    }, [open, focusFirstDropdownItem]);

    /**
     *
     * Close the menu if the user clicks outside of it.
     *
     * Mimics native “blur” behavior so the dropdown doesn't stay open.
     *
     */
    useEffect(() => {
        const onClick = e => {
            if (
                open &&
                listRef.current &&
                !listRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', onClick);

        return () => {
            document.removeEventListener('mousedown', onClick);
        };
    }, [open, closeMenu]);

    /**
     * Moves focus to another item in the dropdown menu with circular navigation
     * @param {Array<HTMLElement>} nodes - Array of list item nodes
     * @param {number} currentIndex - Current focused item index
     * @param {number} direction - Direction to move (-1 for up, 1 for down)
     */
    const moveFocus = (nodes, currentIndex, direction) => {
        let nextIndex = currentIndex + direction;

        if (nextIndex < 0) {
            nextIndex = nodes.length - 1;
        } else if (nextIndex >= nodes.length) {
            nextIndex = 0;
        }

        getFocusableElement(nodes[nextIndex])?.focus();
    };

    /**
     *
     * handleListKeyDown
     *
     * Keyboard navigation enhancements:
     * - Escape: closes the menu and returns focus to the button.
     * - Tab / Shift+Tab: moves focus forward/backward through items,
     *   closes the menu when navigating past the first or last item.
     * - ArrowUp/ArrowDown: moves focus up/down through items with circular navigation.
     *
     */
    const handleListKeyDown = e => {
        if (!['Escape', 'Tab', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();

            closeMenu();

            return;
        }

        if (!listRef.current) {
            return;
        }

        const activeElement = document.activeElement;
        const nodes = Array.from(listRef.current.children);

        if (nodes.length === 0 || !activeElement) {
            return;
        }

        const closestListItem = activeElement.closest('li');

        if (!closestListItem) {
            focusFirstDropdownItem();

            return;
        }

        const currentIndex = nodes.indexOf(closestListItem);

        if (currentIndex === -1) {
            focusFirstDropdownItem();

            return;
        }

        if (e.key === 'Tab') {
            const isAtFirst = currentIndex === 0;
            const isAtLast = currentIndex === nodes.length - 1;
            const isMovingBackward = e.shiftKey;

            e.preventDefault();

            if ((isAtFirst && isMovingBackward) || (isAtLast && !isMovingBackward)) {
                closeMenu();

                return;
            }

            moveFocus(nodes, currentIndex, isMovingBackward ? -1 : 1);

            return;
        }

        e.preventDefault();

        if (e.key === 'ArrowUp') {
            moveFocus(nodes, currentIndex, -1);

            return;
        }

        if (e.key === 'ArrowDown') {
            moveFocus(nodes, currentIndex, 1);

            return;
        }
    };

    return (
        <li className={className}>
            <button
                ref={buttonRef}
                onClick={onButtonClick}
                onKeyDown={handleButtonKeyDown}
                id={`${name}_${index}`}
                data-automation-id={name}
                className={`usa-accordion__button usa-nav__link dropdown__pos ${btnClassName}`}
                aria-expanded={open}
                aria-controls={ariaClassName}
            >
                <span>
                    {symbol && <FontAwesomeIcon key={symbol} className={symbolClassName} icon={symbol} />}
                    <FormattedMessage id={text} />
                </span>
                {statesHasFeature && statesHasFeature(environment) && (
                    <FontAwesomeIcon icon="caret-down" data-automation-id={`${name}_${index}_caret`} />
                )}
            </button>
            {React.createElement(
                'ul',
                {
                    className: 'usa-nav__submenu gi-main__nav__submenu',
                    id: ariaClassName,
                    hidden: !open,
                    ref: listRef,
                    onKeyDown: handleListKeyDown,
                    role: 'menu',
                    'aria-orientation': 'vertical',
                },
                children,
            )}
        </li>
    );
};
